import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native'; // Mission 20: Platform detection for web vs native
import { useRouter, useLocalSearchParams, usePathname } from 'expo-router';
import type { Goal, GoalFramework, Plan, Workout, MobilityRoutine, RestDay, HierarchicalData } from '../types';
import { DataService } from '../services/dataService';
import { ContextualChatButton } from '../components/ContextualChatButton';
import { ChatWithCedarIcon } from '../components/ChatWithCedarIcon';
import { sortByTimestamp, getCardTimestampForPlan, formatCardDateShort, formatRelativeDate } from '../utils/dateUtils';
import { WorkoutCategorizationService } from '../services/workoutCategorizationService';
import { storage } from '../services/storage';

// Navigation Hook
import { NavigationProvider, useNavigation } from '../contexts/NavigationContext';
import { useProfile } from '../contexts/ProfileContext';
import { SimpleWorkoutCard } from '../components/cards/SimpleWorkoutCard';
import { SimpleMobilityCard } from '../components/cards/SimpleMobilityCard';
import { ActiveGoalsSection } from '../components/ActiveGoalsSection';
import { ThisWeeksPlanCard } from '../components/cards/ThisWeeksPlanCard';
import { RestDayCardWrapper } from '../components/cards/RestDayCardWrapper';
// Icon imports
import { CardioIcon, StrengthIcon, HIITIcon, YogaIcon, MobilityIcon, RestIcon } from '../components/icons';
import { WorkoutIcon, getWorkoutIconType } from '../utils/workoutIconMapper';
import { CHILD_MOBILITY_RE } from '../utils/sharedFormatters';

// Import all Anima components
// Live Cards
import { CmpPlancard as AnimaPlanCard } from '../anima/Cards/Cards-Live-Plans/src/components/CmpPlancard/CmpPlancard';
import { CmpWorkoutcard as AnimaWorkoutCard } from '../anima/Cards/Cards-Live-Workout/src/components/CmpWorkoutcard/CmpWorkoutcard';
import { CoachStateLive as AnimaMobilityCard } from '../anima/Cards/Cards-Live-Mobility/src/components/CoachStateLive/CoachStateLive';

// Archived Cards
import { CmpPlancard as AnimaArchivedPlanCard } from '../anima/Cards/Cards-Archived-Plans/src/components/CmpPlancard/CmpPlancard';
import { CmpWorkoutcard as AnimaArchivedWorkoutCard } from '../anima/Cards/Cards-Archived-Workout/src/components/CmpWorkoutcard/CmpWorkoutcard';

// Detail Pages
// Detail Pages (using clean React components for better navigation workflow)
import { WorkoutDetail } from './WorkoutDetail';
import { MobilityDetail } from './MobilityDetail';
import { PlanDetail } from './PlanDetail';
import GoalDetail from './GoalDetail';
import RestDayDetail from './RestDayDetail'; // 🛌 JAN 23, 2026: Rest day detail page

// Peek Modals
import { PlanPeekModal as AnimaPlanPeekModal } from '../anima/Cards/Cards-PeekModal-Plans/src/screens/PlanPeekModal/PlanPeekModal';
import { WorkoutPeekModal as AnimaWorkoutPeekModal } from '../anima/Cards/Cards-PeekModal-Workout/src/screens/WorkoutPeekModal/WorkoutPeekModal';
import { MobilityPeekModal as AnimaMobilityPeekModal } from '../anima/Cards/Cards-PeekModal-Mobility/src/screens/MobilityPeekModal/MobilityPeekModal';

// 🔧 MINOR GAP FIX: Framework Details Modal
import { FrameworkDetailsModal } from '../components/modals/FrameworkDetailsModal';

// Bottom Navigation
import { CmpBootomnavCoach as ProperNavCoach } from '../anima/Navigation Component/src/components/CmpBootomnavCoach/CmpBootomnavCoach.jsx';
import '../anima/Navigation Component/src/components/CmpBootomnavCoach/style.css';
import './MyPath.css';

// 🆕 JAN 2025: Mobile optimization styles for sessions view
import '../styles/sessions-mobile.css';
// Card system styles with design tokens
import '../styles/card-system.css';

// PAGINATION CONSTANTS FOR SCALABILITY
const ITEMS_PER_PAGE = 20; // Show 20 items per page for optimal performance

// Remove progress types and keep only essential ones
type ViewMode = 'schedule' | 'browse';
type EntityFilter = 'goals' | 'plans' | 'sessions'; // 🆕 JAN 2025: Simplified to 3 filters

// 🆕 JAN 2025: Session rendering imports (category filtering removed for simplicity)
import { categorizeSession } from '../services/sessionCategorizationService';
import { getRenderingConfig, getStatusBadge, extractMetrics, getQuickStats } from '../services/sessionRenderingService';

// Chat Context Types - Temporarily disabled
// interface ChatContext {
//   type: 'goal' | 'plan' | 'workout' | 'mobility' | 'progress' | 'schedule' | 'general';
//   id?: string;
//   title?: string;
//   description?: string;
//   action?: 'view' | 'complete' | 'modify' | 'question' | 'concern' | 'progress';
//   additionalContext?: string;
// }

// Progress Section Types
// interface ProgressMetric {
//   label: string;
//   value: string | number;
//   change?: string;
//   trend?: 'up' | 'down' | 'stable';
//   icon?: string;
// }

// interface ProgressInsight {
//   type: 'achievement' | 'trend' | 'recommendation' | 'milestone';
//   title: string;
//   description: string;
//   date?: string;
//   icon?: string;
// }

// Centralized Chat Navigation Hook - Temporarily disabled
// const useChatNavigation = () => {
//   const navigate = useNavigate();

//   const navigateToChat = (context: ChatContext, customMessage?: string) => {
//     const contextString = `${context.type}${context.id ? `:${context.id}` : ''}${context.title ? ` - ${context.title}` : ''}`;
    
//     const defaultMessages = {
//       goal: `I'm looking at my goal "${context.title}". Can you help me with this?`,
//       plan: `I have a question about my plan "${context.title}".`,
//       workout: `I'm looking at the workout "${context.title}" and need to discuss something.`,
//       mobility: `I want to talk about the mobility routine "${context.title}".`,
//       progress: `I'd like to discuss my progress and what I'm seeing in my data.`,
//       schedule: `I need help with my schedule and what's coming up.`,
//       general: `I have a question about my fitness journey.`
//     };

//     const message = customMessage || defaultMessages[context.type];
    
//     navigate('/chat', {
//       state: {
//         from: 'move-section',
//         context: contextString,
//         initialMessage: message,
//         contextData: context
//       }
//     });
//   };

//   return { navigateToChat };
// };

// Progress Section Component with Chat Integration
// const ProgressSection: React.FC<{ 
//   data: HierarchicalData;
//   onItemClick: (type: string, id: string) => void;
// }> = ({ data, onItemClick: _onItemClick }) => {
//   const navigate = useNavigate();
//   // const { navigateToChat } = useChatNavigation(); // Will implement chat integration later
//   const [progressData, setProgressData] = useState<{
//     metrics: ProgressMetric[];
//     insights: ProgressInsight[];
//     momentumReport?: any;
//     biometricData?: any;
//   }>({
//     metrics: [],
//     insights: []
//   });
//   const [activeTab, setActiveTab] = useState<'overview' | 'metrics' | 'insights'>('overview');
//   const [isLoading, setIsLoading] = useState(true);

//   useEffect(() => {
//     loadProgressData();
//   }, [data]);

//   const loadProgressData = async () => {
//     setIsLoading(true);
//     try {
//       // Calculate basic metrics from existing data
//       const completedWorkouts = data.workouts.filter(w => w.status === 'completed').length;
//       const totalWorkouts = data.workouts.length;
//       const completedPlans = data.plans.filter(p => p.state === 'completed').length;
//       const activeGoals = data.goals.filter(g => g.status === 'active').length;
      
//       // Calculate consistency (last 30 days)
//       const thirtyDaysAgo = new Date();
//       thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
//       const recentWorkouts = data.workouts.filter(w => 
//         w.completedAt && new Date(w.completedAt) > thirtyDaysAgo
//       );
//       const consistency = Math.round((recentWorkouts.length / 30) * 100);

//       // Basic metrics
//       const metrics: ProgressMetric[] = [
//         {
//           label: 'Workouts Completed',
//           value: completedWorkouts,
//           change: completedWorkouts > 0 ? `${Math.round((completedWorkouts / totalWorkouts) * 100)}% complete` : undefined,
//           trend: 'up',
//           icon: '💪'
//         },
//         {
//           label: 'Active Goals',
//           value: activeGoals,
//           icon: '🎯'
//         },
//         {
//           label: 'Plans Completed',
//           value: completedPlans,
//           icon: '📋'
//         },
//         {
//           label: '30-Day Consistency',
//           value: `${consistency}%`,
//           trend: consistency > 70 ? 'up' : consistency > 40 ? 'stable' : 'down',
//           icon: '🔥'
//         }
//       ];

//       // Generate insights based on data patterns
//       const insights: ProgressInsight[] = [];
      
//       if (completedWorkouts > 0) {
//         insights.push({
//           type: 'achievement',
//           title: 'Workout Milestone',
//           description: `You've completed ${completedWorkouts} workout${completedWorkouts > 1 ? 's' : ''}! Keep up the momentum.`,
//           icon: '🏆'
//         });
//       }

//       if (consistency > 70) {
//         insights.push({
//           type: 'trend',
//           title: 'Excellent Consistency',
//           description: `You've maintained ${consistency}% consistency over the last 30 days. You're building strong habits!`,
//           icon: '📈'
//         });
//       } else if (consistency < 40) {
//         insights.push({
//           type: 'recommendation',
//           title: 'Consistency Opportunity',
//           description: 'Consider setting smaller, more achievable daily goals to build momentum.',
//           icon: '💡'
//         });
//       }

//       // Check for recent completions
//       const recentCompletion = data.workouts
//         .filter(w => w.status === 'completed')
//         .sort((a, b) => new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime())[0];
      
//       if (recentCompletion && recentCompletion.completedAt) {
//         const daysSince = Math.floor((Date.now() - new Date(recentCompletion.completedAt).getTime()) / (1000 * 60 * 60 * 24));
//         if (daysSince <= 1) {
//           insights.push({
//             type: 'achievement',
//             title: 'Recent Success',
//             description: `Great job completing "${recentCompletion.title}" ${daysSince === 0 ? 'today' : 'yesterday'}!`,
//             date: recentCompletion.completedAt.toString(),
//             icon: '✨'
//           });
//         }
//       }

//       // Try to load momentum report
//       try {
//         const momentumResponse = await fetch(`/api/momentum/reports/${localStorage.getItem('userId')}`);
//         const momentumReport = momentumResponse.ok ? await momentumResponse.json() : null;
        
//         if (momentumReport) {
//           insights.push({
//             type: 'trend',
//             title: 'Weekly Momentum',
//             description: momentumReport.summary || 'Your weekly momentum report is ready to review.',
//             icon: '📊'
//           });
//         }
//       } catch (error) {
//         console.log('Momentum report not available');
//       }

//       setProgressData({
//         metrics,
//         insights,
//         momentumReport: null, // Will be populated if available
//         biometricData: null   // Will be populated if available
//       });
//     } catch (error) {
//       console.error('Error loading progress data:', error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const renderMetricCard = (metric: ProgressMetric) => (
//     <div key={metric.label} className="bg-white rounded-lg p-4 border border-gray-100">
//       <div className="flex items-center justify-between mb-2">
//         <span className="text-sm text-gray-600">{metric.label}</span>
//         {metric.icon && <span className="text-lg">{metric.icon}</span>}
//       </div>
//       <div className="flex items-end justify-between">
//         <span className="text-2xl font-bold" style={{ color: 'var(--coloron-surface900)' }}>
//           {metric.value}
//         </span>
//         {metric.change && (
//           <span className={`text-xs px-2 py-1 rounded-full ${
//             metric.trend === 'up' ? 'bg-green-100 text-green-700' :
//             metric.trend === 'down' ? 'bg-red-100 text-red-700' :
//             'bg-gray-100 text-gray-700'
//           }`}>
//             {metric.change}
//           </span>
//         )}
//       </div>
//       <div className="mt-3">
//         <ContextualChatButton
//           context={{
//             type: 'progress',
//             title: metric.label,
//             description: `Current value: ${metric.value}${metric.change ? `, ${metric.change}` : ''}`,
//             action: 'question'
//           }}
//           text="Ask about this"
//           size="small"
//           variant="minimal"
//           className="w-full"
//         />
//       </div>
//     </div>
//   );

//   const renderInsightCard = (insight: ProgressInsight) => (
//     <div key={`${insight.type}-${insight.title}`} className="bg-white rounded-lg p-4 border border-gray-100">
//       <div className="flex items-start space-x-3">
//         {insight.icon && (
//           <span className="text-xl flex-shrink-0 mt-0.5">{insight.icon}</span>
//         )}
//         <div className="flex-1">
//           <h4 className="font-medium text-sm mb-1" style={{ color: 'var(--coloron-surface900)' }}>
//             {insight.title}
//           </h4>
//           <p className="text-sm text-gray-600 leading-relaxed">
//             {insight.description}
//           </p>
//           {insight.date && (
//             <span className="text-xs text-gray-400 mt-2 block">
//               {new Date(insight.date).toLocaleDateString()}
//             </span>
//           )}
//         </div>
//       </div>
//     </div>
//   );

//   const renderOverview = () => (
//     <div className="space-y-6">
//       {/* Key Metrics Grid */}
//       <div>
//         <h3 className="text-base font-semibold mb-3" style={{ color: 'var(--coloron-surface900)' }}>
//           Your Progress
//         </h3>
//         <div className="grid grid-cols-2 gap-3">
//           {progressData.metrics.slice(0, 4).map(renderMetricCard)}
//         </div>
//       </div>

//       {/* Recent Insights */}
//       <div>
//         <h3 className="text-base font-semibold mb-3" style={{ color: 'var(--coloron-surface900)' }}>
//           Recent Insights
//         </h3>
//         <div className="space-y-3">
//           {progressData.insights.slice(0, 3).map(renderInsightCard)}
//         </div>
//       </div>

//       {/* TCX Upload Section */}
//       <div className="bg-gray-50 rounded-lg p-4">
//         <div className="flex items-center justify-between mb-3">
//           <h3 className="text-base font-semibold" style={{ color: 'var(--coloron-surface900)' }}>
//             Share Workout Data
//           </h3>
//           <span className="text-lg">📁</span>
//         </div>
//         <p className="text-sm text-gray-600 mb-3">
//           Upload TCX files or share workout data with Cedar for deeper insights and personalized coaching.
//         </p>
//         <button 
//           onClick={() => navigate('/chat')}
//           className="w-full py-2 px-4 rounded-lg text-white text-sm font-medium transition-colors"
//           style={{
//             backgroundColor: 'var(--colorprimary-500)',
//             fontFamily: 'var(--UI-14-medium-font-family)',
//             fontSize: 'var(--UI-14-medium-font-size)',
//             fontWeight: 'var(--UI-14-medium-font-weight)'
//           }}
//         >
//           Share with Cedar
//         </button>
//       </div>
//     </div>
//   );

//   const renderMetrics = () => (
//     <div className="space-y-4">
//       <div className="grid grid-cols-1 gap-3">
//         {progressData.metrics.map(renderMetricCard)}
//       </div>
      
//       {/* Placeholder for future biometric data */}
//       <div className="bg-gray-50 rounded-lg p-4 text-center">
//         <div className="text-gray-400 mb-2">
//           <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
//           </svg>
//         </div>
//         <p className="text-sm text-gray-600 mb-3">Connect devices for advanced metrics</p>
//         <p className="text-xs text-gray-500">VO2 Max, Heart Rate Zones, Power Data & More</p>
//       </div>
//     </div>
//   );

//   const renderInsights = () => (
//     <div className="space-y-3">
//       {progressData.insights.map(renderInsightCard)}
      
//       {progressData.insights.length === 0 && (
//         <div className="bg-gray-50 rounded-lg p-4 text-center">
//           <div className="text-gray-400 mb-2">
//             <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
//             </svg>
//           </div>
//           <p className="text-sm text-gray-600">Complete more activities to unlock insights</p>
//         </div>
//       )}
//     </div>
//   );

//   if (isLoading) {
//     return (
//       <div className="mb-6">
//         <h2 className="text-lg font-semibold mb-3" style={{
//           fontFamily: 'var(--text-h2good-font-family)',
//           fontSize: 'var(--text-h2good-font-size)',
//           fontWeight: 'var(--text-h2good-font-weight)',
//           color: 'var(--coloron-surface900)'
//         }}>
//           Your Progress
//         </h2>
//         <div className="bg-gray-50 rounded-lg p-4 text-center">
//           <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-green-600 rounded-full mx-auto mb-2"></div>
//           <p className="text-sm text-gray-600">Loading your progress...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="mb-6">
//       <h2 className="text-lg font-semibold mb-3" style={{
//         fontFamily: 'var(--text-h2good-font-family)',
//         fontSize: 'var(--text-h2good-font-size)',
//         fontWeight: 'var(--text-h2good-font-weight)',
//         color: 'var(--coloron-surface900)'
//       }}>
//         Your Progress
//       </h2>

//       {/* Tab Navigation */}
//       <div className="flex space-x-1 mb-4 bg-gray-100 rounded-lg p-1">
//         {[
//           { key: 'overview', label: 'Overview' },
//           { key: 'metrics', label: 'Metrics' },
//           { key: 'insights', label: 'Insights' }
//         ].map(tab => (
//           <button
//             key={tab.key}
//             onClick={() => setActiveTab(tab.key as any)}
//             className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
//               activeTab === tab.key
//                 ? 'bg-white text-gray-900 shadow-sm'
//                 : 'text-gray-600 hover:text-gray-900'
//             }`}
//             style={{
//               fontFamily: 'var(--UI-14-medium-font-family)',
//               fontSize: 'var(--UI-14-medium-font-size)',
//               fontWeight: activeTab === tab.key ? 'var(--UI-14-medium-font-weight)' : 'normal'
//             }}
//           >
//             {tab.label}
//           </button>
//         ))}
//       </div>

//       {/* Tab Content */}
//       {activeTab === 'overview' && renderOverview()}
//       {activeTab === 'metrics' && renderMetrics()}
//       {activeTab === 'insights' && renderInsights()}
//     </div>
//   );
// };

// Chat Invitation Modal Component
const ChatInvitationModal: React.FC<{
  workout: Workout;
  onChat: () => void;
  onDismiss: () => void;
}> = ({ workout, onChat, onDismiss }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onDismiss}>
    <div className="bg-white rounded-lg p-6 m-4 max-w-sm" onClick={e => e.stopPropagation()}>
      <h3 className="text-lg font-semibold mb-2">Great work!</h3>
      <p className="text-gray-600 mb-4">
        You completed "{workout.title}". Want to share how it went with Cedar?
      </p>
      <div className="flex space-x-3">
        <button
          onClick={onChat}
          className="flex-1 bg-green-700 text-white py-2 px-4 rounded-lg text-sm font-medium"
        >
          Chat with Cedar
        </button>
        <button
          onClick={onDismiss}
          className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium"
        >
          Maybe later
        </button>
      </div>
    </div>
  </div>
);

const TodaysFocus: React.FC<{
  onItemClick: (type: string, id: string) => void;
  onWorkoutComplete: (workout: Workout) => void
}> = ({ onItemClick, onWorkoutComplete }) => {
  const todaysFocus = DataService.getTodaysFocus();

  // Get today's specific items only
  const { workouts: todaysWorkouts, mobility: todaysMobility, restDays: todaysRestDays } = todaysFocus;

  const hasToday = todaysWorkouts.length > 0 || todaysMobility.length > 0 || todaysRestDays.length > 0;

  if (!hasToday) {
    return (
      <div className="mb-6 mt-4 px-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-3">
          Today's Focus
        </h2>
        <div style={{
          background: 'var(--cedar-subtle)',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <div style={{ color: 'var(--cedar-primary)', marginBottom: '12px' }}>
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p style={{
            color: 'var(--coloron-surface900)',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 500
          }}>Today's activities are complete!</p>
        </div>
      </div>
    );
  }

  // Helper to get trimmed title (text before colon)
  const trimTitle = (title: string) => {
    const colonIndex = title.indexOf(':');
    return colonIndex > 0 ? title.substring(0, colonIndex).trim() : title;
  };

  // Helper to get distance from workout data (aligned with SimpleWorkoutCard null-safe pattern)
  const getDistance = (workout: Workout) => {
    const wd = (workout as any).workoutData || (workout as any).workout_data;
    if (wd?.distance) {
      const val = wd.distance.value != null ? wd.distance.value : (typeof wd.distance === 'number' ? wd.distance : null);
      const unit = wd.distance.unit || 'km';
      if (val != null && val > 0) {
        return `${val}${unit}`;
      }
    }
    // Check top-level distance fields
    if ((workout as any).distance_km && (workout as any).distance_km > 0) {
      return `${(workout as any).distance_km}km`;
    }
    return null;
  };

  // Determine if workout is cardio type
  const isCardio = (workout: Workout) => {
    const type = ((workout as any).activityType || workout.type || '').toLowerCase();
    return type === 'cardio' || type === 'running' || type === 'cycling' || type === 'swimming' || type === 'walking';
  };

  return (
    <div className="mb-6 mt-4 px-4">
      <h2 className="text-xl font-semibold text-gray-900 mb-3">
        Today's Focus
      </h2>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-50">
        {/* Rest Days */}
        {todaysRestDays.map(restDay => {
          const isCompleted = restDay.status === 'completed';
          return (
            <div
              key={restDay.id}
              onClick={() => onItemClick('restDay', restDay.id)}
              className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              {/* REST badge */}
              <span
                className="text-[11px] font-bold px-2 py-1 rounded mr-3 flex-shrink-0"
                style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.12)' }}
              >
                REST
              </span>
              {/* Title */}
              <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                {restDay.title || 'Rest Day'}
              </span>
              {/* Completion indicator */}
              <div className="flex-shrink-0 ml-2">
                {isCompleted ? (
                  <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
            </div>
          );
        })}

        {/* Workouts - compact rows */}
        {todaysWorkouts.map(workout => {
          const isCompleted = workout.status === 'completed';
          const activityType = ((workout as any).activityType || workout.type || '').toLowerCase();
          const duration = (workout as any).durationMinutes || (workout as any).duration_minutes || (workout as any).duration;
          const distance = isCardio(workout) ? getDistance(workout) : null;
          const iconType = getWorkoutIconType(activityType, (workout as any).activityType || '');

          return (
            <div
              key={workout.id}
              onClick={() => onItemClick('workout', workout.id)}
              className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              {/* Workout icon */}
              <div className="flex-shrink-0 mr-3">
                <WorkoutIcon iconType={iconType} size={20} />
              </div>
              {/* Title */}
              <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                {trimTitle(workout.title)}
              </span>
              {/* Metrics */}
              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                {distance && (
                  <span className="text-xs text-gray-500">{distance}</span>
                )}
                {duration && (
                  <span className="text-xs text-gray-500">{duration}m</span>
                )}
                {/* Completion indicator */}
                {isCompleted ? (
                  <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ) : (
                  <div
                    className="w-5 h-5 rounded-full border-2 border-gray-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      onWorkoutComplete(workout);
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* Mobility - compact rows */}
        {todaysMobility.map(routine => {
          const isCompleted = routine.status === 'completed';
          const duration = (routine as any).durationMinutes || (routine as any).duration_minutes || (routine as any).duration;
          const modality = ((routine as any).modality || routine.type || '').toLowerCase();
          const isYoga = modality === 'yoga' || modality.includes('yoga');

          return (
            <div
              key={routine.id}
              onClick={() => onItemClick('mobility', routine.id)}
              className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              {/* Icon */}
              <div className="flex-shrink-0 mr-3">
                <WorkoutIcon iconType={isYoga ? 'yoga' : 'stretching'} size={20} />
              </div>
              {/* Title */}
              <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                {trimTitle(routine.title)}
              </span>
              {/* Duration + completion */}
              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                {duration && (
                  <span className="text-xs text-gray-500">{duration}m</span>
                )}
                {isCompleted ? (
                  <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 🎯 FIX #1-6: This Week's Plan component extracted to ThisWeeksPlanCard.tsx
// Now reusable in both Today section (MyPath) and Goal Detail view

const MobilityCardWrapper: React.FC<{
  routine: MobilityRoutine;
  isArchived: boolean;
  onTap: () => void;
  onLongPress: () => void;
  showTodayBadge?: boolean;
  showDateBadge?: boolean;
}> = ({ routine, isArchived, onTap, onLongPress, showTodayBadge = false, showDateBadge = false }) => {
  
  // Directly use SimpleMobilityCard for consistency
  return (
    <SimpleMobilityCard
      routine={routine}
      isArchived={isArchived}
      onTap={onTap}
      onLongPress={onLongPress}
      onComplete={(routine) => {
        // Handle mobility routine completion
        console.log('Mobility routine completed:', routine.id);
      }}
      showTodayBadge={showTodayBadge}
      showDateBadge={showDateBadge}
    />
  );
};

// Missing GoalCardWrapper component
const GoalCardWrapper: React.FC<{
  goal: Goal;
  isArchived: boolean;
  onTap: () => void;
  onLongPress: () => void;
  onViewFramework?: (framework: GoalFramework, goalTitle: string) => void;
}> = ({ goal, isArchived, onTap, onLongPress, onViewFramework }) => {
  const getGoalCategory = () => {
    // Priority 1: framework activityType (LLM-classified after background pipeline)
    // Priority 2: iconType (set by extractor at goal creation time)
    // Priority 3: keyword fallback
    const activityType = goal.framework?.activityType || goal.iconType || '';

    // Endurance / cardio activities
    if (['running', 'cycling', 'swimming', 'rowing', 'walking', 'hiking'].includes(activityType)) {
      const labelMap: Record<string, string> = {
        running: 'Running', cycling: 'Cycling', swimming: 'Swimming',
        rowing: 'Rowing', walking: 'Walking', hiking: 'Hiking',
      };
      return {
        label: labelMap[activityType] || 'Endurance',
        color: 'bg-blue-100 text-blue-700',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5v6l4 2M5 3l-2 2 2 2M19 3l2 2-2 2M12 22a9 9 0 100-18 9 9 0 000 18z" />
          </svg>
        ),
      };
    }

    // Strength / resistance activities
    if (['strength', 'weightlifting', 'bodyweight', 'crossfit', 'plyometrics'].includes(activityType)) {
      return {
        label: 'Strength',
        color: 'bg-orange-100 text-orange-700',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        ),
      };
    }

    // Flexibility / mind-body
    if (['yoga', 'stretching'].includes(activityType)) {
      return {
        label: 'Flexibility',
        color: 'bg-teal-100 text-teal-700',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        ),
      };
    }

    // HIIT / sport-specific
    if (activityType === 'hiit') {
      return {
        label: 'HIIT',
        color: 'bg-red-100 text-red-700',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        ),
      };
    }

    if (['team-sports', 'racquet-sports', 'golf', 'martial-arts', 'sports'].includes(activityType)) {
      return {
        label: activityType === 'sports' ? 'Sports' : activityType.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        color: 'bg-indigo-100 text-indigo-700',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 10v4a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      };
    }

    // Keyword fallback (for goals created before iconType was introduced, or lifestyle goals)
    const title = goal.title.toLowerCase();
    const description = goal.description.toLowerCase();
    if (title.includes('weight') || title.includes('lose') || description.includes('weight loss')) {
      return {
        label: 'Weight Loss',
        color: 'bg-purple-100 text-purple-700',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        ),
      };
    }

    // Lifestyle / general
    return {
      label: activityType === 'lifestyle' ? 'Lifestyle' : 'Fitness',
      color: 'bg-green-100 text-green-700',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
    };
  };

  const getTimeframe = () => {
    // 🔧 JAN 2025: Precise timeframe formatting per user requirements
    // Formats: "1 Year, 1 Month" | "9 Months" | "1 Month, 22 days" | "22 days"
    if (goal.targetDate) {
      const today = new Date();
      const target = new Date(goal.targetDate);
      const totalDays = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      if (totalDays <= 0) return "Complete";
      
      // Calculate years, months, and remaining days
      const years = Math.floor(totalDays / 365);
      const remainingAfterYears = totalDays - (years * 365);
      const months = Math.floor(remainingAfterYears / 30);
      const days = remainingAfterYears - (months * 30);
      
      // Over 1 year: "1 Year, 1 Month"
      if (years > 0) {
        if (months > 0) {
          return `${years} Year${years > 1 ? 's' : ''}, ${months} Month${months > 1 ? 's' : ''}`;
        }
        return `${years} Year${years > 1 ? 's' : ''}`;
      }
      
      // Less than 1 year, more than 2 months: "9 Months"
      if (totalDays >= 60) {
        const totalMonths = Math.round(totalDays / 30);
        return `${totalMonths} Month${totalMonths > 1 ? 's' : ''}`;
      }
      
      // Less than 2 months, more than 1 month: "1 Month, 22 days"
      if (totalDays >= 30) {
        const monthCount = Math.floor(totalDays / 30);
        const dayCount = totalDays - (monthCount * 30);
        if (dayCount > 0) {
          return `${monthCount} Month${monthCount > 1 ? 's' : ''}, ${dayCount} day${dayCount > 1 ? 's' : ''}`;
        }
        return `${monthCount} Month${monthCount > 1 ? 's' : ''}`;
      }
      
      // Less than 1 month: "22 days"
      return `${totalDays} day${totalDays > 1 ? 's' : ''}`;
    }
    
    // Fallback: Calculate from journey total weeks
    if (goal.journey?.totalEstimatedWeeks) {
      const weeks = goal.journey.totalEstimatedWeeks;
      if (weeks <= 8) return `${weeks} weeks`;
      if (weeks <= 52) {
        const months = Math.round(weeks / 4.33);
        return `${months} month${months > 1 ? 's' : ''}`;
      }
      return "Long-term";
    }
    
    return "Long-term";
  };

  const getCurrentPhaseInfo = () => {
    if (goal.journey?.phases && goal.journey.currentPhase < goal.journey.phases.length) {
      const currentPhase = goal.journey.phases[goal.journey.currentPhase];
      return `Phase ${goal.journey.currentPhase + 1}: ${currentPhase.title}`;
    }
    return null;
  };

  const category = getGoalCategory();
  const timeframe = getTimeframe();
  const currentPhase = getCurrentPhaseInfo();

  // Get status badge class
  const getStatusBadgeClass = () => {
    if (goal.status === 'active') return 'mypath-status-badge active';
    if (goal.status === 'completed') return 'mypath-status-badge completed';
    return 'mypath-status-badge paused';
  };

  return (
    <div 
      className="mypath-card mypath-goal-card"
      onClick={onTap}
      onContextMenu={(e) => {
        e.preventDefault();
        onLongPress();
      }}
    >
      {/* Header with category and timeframe */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2 min-w-0">
          <span className="mypath-goal-tag flex items-center space-x-1 shrink-0">
            <span className="mypath-goal-card-icon-wrap" style={{ marginRight: 6 }}>{category.icon}</span>
            <span>{category.label}</span>
          </span>
          <span className="mypath-card-label">{timeframe}</span>
        </div>
        {/* Chat button */}
                  <ContextualChatButton
            context={{
              type: 'goal',
              id: goal.id,
              title: goal.title,
              description: goal.description,
              action: 'question'
            }}
            text=""
            size="small"
            customMessage={`I want to discuss my goal: ${goal.title}. ${currentPhase ? `I'm currently in ${currentPhase.toLowerCase()}.` : ''}`}
          />
      </div>

      {/* 🎨 JAN 5, 2026: LLM-generated icon at top-left (same as session cards) */}
      {goal.iconType && (
        <div className="mb-3 flex items-center">
          <span className="mypath-goal-card-icon-wrap" style={{ marginRight: 10 }}>
            <WorkoutIcon 
              type={goal.iconType} 
              size={32} 
            />
          </span>
        </div>
      )}

      {/* Goal title and brief description */}
      <div className="mb-3 min-w-0">
        <h3 className="mypath-card-title mb-1">{goal.title}</h3>
        {/* 🔧 JAN 2025: Display one-sentence summary for goal card, fallback to briefing summary or description */}
        {/* Full briefing is only visible in GoalDetail drill-down */}
        <p className="mypath-card-description line-clamp-3">
          {goal.briefingOneSentence || goal.briefingSummary || goal.description}
        </p>
      </div>

      {/* Current phase info */}
      {currentPhase && (
        <div className="mb-3">
          <span className="mypath-goal-tag" style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-accent-sky)' }}>
            {currentPhase}
          </span>
        </div>
      )}

      {/* Stats row */}
      <div className="flex items-center justify-between text-xs" style={{ color: 'var(--coloron-surface600)' }}>
        <div className="flex items-center space-x-3">
          <span>
            {goal.progress?.weeklyPlansCompleted || 0} weeks completed
          </span>
          <span>
            {goal.progress?.keyMilestones?.filter(m => m.isCompleted).length || 0} milestones reached
          </span>
        </div>
        <span className={getStatusBadgeClass()}>
          {goal.status === 'active' ? 'Active' : goal.status === 'completed' ? 'Complete' : 'Paused'}
        </span>
      </div>

      {/* Removed Goal Tag section to fix relatedGoals undefined error */}
    </div>
  );
};

// Missing PlanCardWrapper component
const PlanCardWrapper: React.FC<{
  plan: Plan;
  isArchived: boolean;
  onTap: () => void;
  onLongPress: () => void;
  enhanced?: boolean;
}> = ({ plan, isArchived, onTap, onLongPress, enhanced = false }) => {
  const { profile } = useProfile(); // 🆕 JAN 19, 2026: Use profile for personalized title
  const AnimaComponent = isArchived ? AnimaArchivedPlanCard : AnimaPlanCard;
  const [planData, setPlanData] = useState<{
    workouts: any[];
    mobilityRoutines: any[];
    restDays: any[];
  } | null>(null);
  
  const getStatusColor = () => {
    switch (plan.state) {
      case 'active': return '#22c55e';
      case 'completed': return '#3b82f6';
      case 'paused': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const createSubtitle = () => {
    const timestampInfo = getCardTimestampForPlan(plan);
    const formattedDate = formatRelativeDate(timestampInfo.date);
    return `${timestampInfo.label} ${formattedDate}`;
  };

  // Load plan data for enhanced view
  useEffect(() => {
    if (enhanced) {
      const loadPlanData = async () => {
        try {
          const data = await DataService.getAllData();
          // 🔧 FEB 2 (Bug #61): Filter out rest_day workouts (recovery activities shown in rest day drawer)
          const planWorkouts = data.workouts.filter((w: any) => w.planId === plan.id && w.context !== 'rest_day');
          // 🔧 JAN 25: Filter out component mobility (warmup/cooldown children) AND rest_day mobility
          // Check context for both component and rest_day (either indicates child)
          const planMobility = data.mobilityRoutines.filter((m: any) => 
            m.planId === plan.id && m.context !== 'component' && m.context !== 'rest_day' && !m.workout_id
          );
          const planRestDays = data.restDays.filter((r: any) => r.planId === plan.id);
          
          setPlanData({
            workouts: planWorkouts,
            mobilityRoutines: planMobility,
            restDays: planRestDays
          });
        } catch (error) {
          console.error('Failed to load plan data:', error);
        }
      };

      loadPlanData();
    }
  }, [enhanced, plan.id]);

  // Enhanced Browse Plans design
  if (enhanced) {
    // Get related goals for context
    const relatedGoals = plan.goalIds?.map(goalId => DataService.getGoalById(goalId)).filter((goal): goal is NonNullable<typeof goal> => goal !== null && goal !== undefined) || [];
    
    // Use loaded plan data or return loading state
    if (!planData) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      );
    }
    
    const planWorkouts = planData.workouts;
    const planMobility = planData.mobilityRoutines;
    const planRestDays = planData.restDays;
    
    // 🔧 FIX JAN 28, 2026: Calculate CURRENT CALENDAR WEEK boundaries (Monday-Sunday)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    const todayDayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    
    // Calculate Monday of this week
    // If today is Sunday (0), go back 6 days. Otherwise go back (dayOfWeek - 1) days
    const daysToMonday = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1;
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - daysToMonday);
    currentWeekStart.setHours(0, 0, 0, 0);
    // 🔧 FIX FEB 9: Use LOCAL date (not UTC) to avoid timezone shift
    // Problem: .toISOString() converts to UTC, shifting dates by hours (e.g., 5h for EST)
    const currentWeekStartStr = `${currentWeekStart.getFullYear()}-${String(currentWeekStart.getMonth() + 1).padStart(2, '0')}-${String(currentWeekStart.getDate()).padStart(2, '0')}`;
    
    // Calculate Sunday of this week (Monday + 6 days)
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    currentWeekEnd.setHours(23, 59, 59, 999);
    // 🔧 FIX FEB 9: Use LOCAL date (not UTC)
    const currentWeekEndStr = `${currentWeekEnd.getFullYear()}-${String(currentWeekEnd.getMonth() + 1).padStart(2, '0')}-${String(currentWeekEnd.getDate()).padStart(2, '0')}`;

    // 🔧 FIX JAN 23: Filter to CURRENT CALENDAR WEEK ONLY (not plan range)
    // 🔧 FIX FEB 9, 2026: Helper to get LOCAL date string from any date input
    // CRITICAL: When input is a "YYYY-MM-DD" string, extract directly (substring)
    // to avoid new Date("2026-02-10") = UTC midnight → EST getDate() = Feb 9 (WRONG!)
    const toLocalDateStr = (dateInput: string | Date): string => {
      if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}/)) {
        return dateInput.substring(0, 10);
      }
      const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const thisWeekWorkouts = planWorkouts.filter((w: any) => {
      if (!w.scheduledDate) return false;
      const workoutDateStr = toLocalDateStr(w.scheduledDate);
      return workoutDateStr >= currentWeekStartStr && workoutDateStr <= currentWeekEndStr;
    });
    
    const thisWeekMobility = planMobility.filter((m: any) => {
      if (!m.scheduledDate) return false;
      const mobDateStr = toLocalDateStr(m.scheduledDate);
      return mobDateStr >= currentWeekStartStr && mobDateStr <= currentWeekEndStr;
    });
    
    const thisWeekRestDays = planRestDays.filter((r: any) => {
      if (!r.scheduledDate) return false;
      const restDateStr = toLocalDateStr(r.scheduledDate);
      return restDateStr >= currentWeekStartStr && restDateStr <= currentWeekEndStr;
    });

    const thisWeekCompletedActivities = thisWeekWorkouts.filter((w: any) => w.status === 'completed').length + 
                                       thisWeekMobility.filter((m: any) => m.status === 'completed').length +
                                       thisWeekRestDays.filter((r: any) => r.status === 'completed').length;

    // Get current week and day info
    const weekNumber = plan.weekNumber || 1;
    const currentDay = Math.min(thisWeekCompletedActivities + 1, 7);

    // Create goal-driven title (like Browse Plans design)
    const getGoalDrivenTitle = () => {
      if (relatedGoals.length === 0) {
        return `Week ${weekNumber}`;
      } else if (relatedGoals.length === 1) {
        // Single goal: Use goal name in title
        return `${relatedGoals[0].title} • Week ${weekNumber}`;
      } else {
        // Multiple goals: Smart synthesis
        return `Multi-Goal Training • Week ${weekNumber}`;
      }
    };

    return (
      <div className="relative">
        {/* Subtle Goal Tagging - Clear Goal Label for Multiple Goals */}
        {relatedGoals.length > 0 && (
          <div className="flex items-center gap-1 mb-2 flex-wrap">
            {relatedGoals.map((goal, index) => (
              <span 
                key={goal.id}
                className="mypath-goal-tag"
              >
                Goal: {goal.title}
              </span>
            ))}
          </div>
        )}

        <div 
          className="relative mypath-card shadow-sm cursor-pointer"
          style={{ 
            background: 'white', // Override gradient (must use 'background' not 'backgroundColor')
            border: '1px solid var(--cedar-limestone)',
            minHeight: '140px'
          }}
          onClick={onTap}
          onContextMenu={(e) => {
            e.preventDefault();
            onLongPress();
          }}
        >
          {/* Header with dynamic title and subhead (matching Today card) */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              {/* 🎯 JAN 19, 2026: Dynamic title with user name and week date (matching Today card) */}
              <h3 className="mypath-card-title mb-1" style={{ fontSize: '18px' }}>
                {(() => {
                  const monday = new Date(plan.weekStartDate);
                  const monthName = monday.toLocaleDateString('en-US', { month: 'short' });
                  const day = monday.getDate();
                  
                  // 🔧 JAN 25: Check BOTH camelCase AND snake_case (API inconsistency)
                  const userName = (profile as any)?.preferredName || 
                                  (profile as any)?.preferred_name || 
                                  (profile as any)?.firstName || 
                                  (profile as any)?.first_name || 
                                  'Your';
                  
                  console.log('🔍 MyPath Browse: User name resolution:', {
                    profile,
                    userName,
                    allProfileKeys: profile ? Object.keys(profile) : [],
                    fullProfile: profile ? JSON.stringify(profile) : null
                  });
                  
                  // Possessive apostrophe logic
                  let possessive = '';
                  if (userName !== 'Your') {
                    possessive = userName.endsWith('s') ? "'" : "'s";
                  }
                  
                  const dayWithSuffix = day === 1 || day === 21 || day === 31 ? `${day}st` 
                    : day === 2 || day === 22 ? `${day}nd`
                    : day === 3 || day === 23 ? `${day}rd`
                    : `${day}th`;
                  return `${userName}${possessive} week of ${monthName} ${dayWithSuffix}`;
                })()}
              </h3>
              {/* 🎯 JAN 19, 2026: Creative LLM-generated subhead (matching Today card) */}
              {(() => {
                const subhead = (typeof plan.weekSubhead === 'string' ? plan.weekSubhead : null)
                  || (typeof plan.weekOverview === 'string' ? plan.weekOverview : null)
                  || (typeof plan.description === 'string' ? plan.description : null);
                return subhead && (
                  <p className="mypath-card-description mb-2 italic" style={{ fontSize: '14px' }}>
                    {subhead}
                  </p>
                );
              })()}
            </div>
            {/* 🎨 JAN 19, 2026: Chat with Cedar button - use 'clean' variant (matching Today card) */}
            <ContextualChatButton
              context={{
                type: 'plan',
                id: plan.id,
                title: plan.title,
                description: plan.description,
                action: 'question'
              }}
              text=""
              size="small"
              variant="clean"
              customMessage={`I want to discuss this week's plan: ${plan.title}. I'm currently on day ${Math.min(currentDay, 7)} of 7.`}
            />
          </div>

          {/* 🔧 FIX JAN 28, 2026: Week dots - Monday-Sunday (consistent with ThisWeeksPlanCard and PlanDetail) */}
          <div className="mb-3 pb-3 border-b border-gray-100">
            <div className="flex justify-between items-end">
              {Array.from({ length: 7 }, (_, dayIndex) => {
                // dayIndex: 0=Monday, 1=Tuesday, ..., 6=Sunday
                const dayDate = new Date(currentWeekStart);
                dayDate.setDate(currentWeekStart.getDate() + dayIndex);
                // 🔧 FIX FEB 9: Use LOCAL date (not UTC) to avoid timezone shift
                const dayDateStr = toLocalDateStr(dayDate);
                
                // Helper to get sessions for this specific day
                const getSessionsForDay = (dayDateStr: string) => {
                  const allSessions = [
                    ...thisWeekWorkouts.map(w => ({ ...w, sessionType: 'workout' })),
                    ...thisWeekMobility.map(m => ({ ...m, sessionType: 'mobility' })),
                    ...thisWeekRestDays.map(r => ({ ...r, sessionType: 'rest' }))
                  ];
                  
                  return allSessions.filter((session: any) => {
                    if (!session.scheduledDate) return false;
                    // 🔧 FIX FEB 9: Use LOCAL date (not UTC) to avoid timezone shift
                    const sessionDateStr = toLocalDateStr(session.scheduledDate);
                    return sessionDateStr === dayDateStr;
                  });
                };
                
                const daySessions = getSessionsForDay(dayDateStr);
                const hasWorkouts = daySessions.length > 0;
                const allDone = daySessions.length > 0 && daySessions.every((s: any) => s.status === 'completed');
                const isToday = dayDateStr === todayStr;
                const isPast = dayDateStr < todayStr;
                
                // Dot color system (matches native ThisWeeksPlanCard):
                //   Teal (#42A5A5)         = rest day (always, even if past)
                //   Cedar Prime (#1F6C4F)  = completed past day + white checkmark
                //   Cedar Mist (#A8D1BF)   = missed/incomplete past workout day
                //   Cedar Light (#5FA287)  = today/future with workout
                //   Gray (#E0E0E0)         = no activity scheduled

                // Filter out component mobility FIRST so dot color + icon use the same data
                const filteredSessions = daySessions.filter((s: any) => {
                  if (s.sessionType === 'mobility') {
                    if (s.context === 'component' || s.context === 'rest_day' || s.workout_id) return false;
                    const t = (s.title || '').toLowerCase();
                    if (CHILD_MOBILITY_RE.test(t)) return false;
                  }
                  return true;
                });
                const hasRestDay = filteredSessions.some((s: any) => s.sessionType === 'rest');

                let dotColor = 'bg-[#E0E0E0]'; // Default: no activity
                let showCheckmark = false;

                if (hasRestDay) {
                  dotColor = 'bg-[#42A5A5]'; // Teal — rest day (always)
                } else if (isPast && hasWorkouts && allDone) {
                  dotColor = 'bg-[#1F6C4F]'; // Cedar Prime — completed
                  showCheckmark = true;
                } else if (isPast && hasWorkouts) {
                  dotColor = 'bg-[#A8D1BF]'; // Cedar Mist — missed/incomplete
                } else if (hasWorkouts) {
                  dotColor = 'bg-[#5FA287]'; // Cedar Light — today/future with workout
                }
                
                // 🎨 FIX JAN 23: Today is 50% larger (consistent everywhere)
                const dotSize = isToday ? 'w-[18px] h-[18px]' : 'w-3 h-3';
                const dayAbbrevs = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
                
                // Get workout icon component
                const getWorkoutIconComponent = (session: any) => {
                  // 🔧 JAN 25: Show "REST" badge for rest days (matches ThisWeeksPlanCard)
                  if (session.sessionType === 'rest') {
                    return (
                      <span 
                        className="text-[10px] font-semibold px-1 py-0.5 rounded"
                        style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.1)' }}
                      >
                        REST
                      </span>
                    );
                  }
                  
                  if (session.sessionType === 'mobility') {
                    const modality = (session.modality || session.type || '').toLowerCase();
                    if (modality === 'yoga' || modality.includes('yoga')) {
                      return <WorkoutIcon iconType="yoga" size={16} className="flex-shrink-0" />;
                    }
                    if (modality === 'static-stretching' || modality.includes('stretch')) {
                      return <WorkoutIcon iconType="stretching" size={16} className="flex-shrink-0" />;
                    }
                    return <WorkoutIcon iconType="stretching" size={16} className="flex-shrink-0" />;
                  }
                  
                  const workoutType = session.type || session.workout_type || '';
                  const activityType = session.activityType || '';
                  const iconType = getWorkoutIconType(workoutType, activityType);
                  const intensity = session.intensity || 0.5;
                  
                  return (
                    <WorkoutIcon
                      iconType={iconType}
                      title={session.title || session.name || ''}
                      size={16}
                      className="flex-shrink-0"
                    />
                  );
                };

                return (
                  <div key={dayIndex} className="flex flex-col items-center flex-1">
                    {/* 🎨 Dot with optional checkmark */}
                    <div className="relative mb-1">
                      <div className={`rounded-full transition-all duration-200 ${dotSize} ${dotColor}`} />
                      {showCheckmark && (
                        <svg 
                          className="absolute inset-0 m-auto" 
                          width={isToday ? "12" : "8"} 
                          height={isToday ? "12" : "8"} 
                          viewBox="0 0 12 12" 
                          fill="none"
                        >
                          <path 
                            d="M10 3L4.5 8.5L2 6" 
                            stroke="white" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    
                    {/* Primary activity icon below dot — one icon per day (matches native) */}
                    {hasWorkouts && filteredSessions.length > 0 && (
                      <div className="flex items-center gap-px mb-1 justify-center">
                        {(() => {
                          // Rest day takes priority
                          const restSession = filteredSessions.find((s: any) => s.sessionType === 'rest');
                          if (restSession) {
                            return (
                              <span
                                className="text-[10px] font-semibold px-1 py-0.5 rounded"
                                style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.1)' }}
                              >
                                REST
                              </span>
                            );
                          }
                          // Pick primary: strength > hiit > cardio > mobility
                          // Resolve to canonical icon type FIRST so "full-body-strength" → "weightlifting"
                          const PRIORITY = ['weightlifting', 'bodyweight', 'core', 'crossfit', 'hiit', 'running', 'cycling', 'swimming', 'walking'];
                          const primary = [...filteredSessions]
                            .filter((s: any) => s.sessionType !== 'mobility')
                            .sort((a: any, b: any) => {
                              const aType = getWorkoutIconType(a.type || a.activityType || '', a.title || '');
                              const bType = getWorkoutIconType(b.type || b.activityType || '', b.title || '');
                              const aIdx = PRIORITY.indexOf(aType);
                              const bIdx = PRIORITY.indexOf(bType);
                              return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
                            })[0] || filteredSessions[0];
                          return <div>{getWorkoutIconComponent(primary)}</div>;
                        })()}
                      </div>
                    )}
                    
                    {/* Day Letter - Bold for today */}
                    <span className={`text-xs text-gray-500 ${isToday ? 'font-bold' : 'font-medium'}`}>
                      {dayAbbrevs[dayIndex]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 🎯 JAN 25: 2-Row Activity Summary - Count in text beside icon */}
          {(() => {
            // Classify workouts using getWorkoutIconType (same as native + dot icons)
            const STRENGTH_ICONS = new Set(['weightlifting', 'core', 'bodyweight', 'crossfit', 'hiit']);
            const MOBILITY_ICONS = new Set(['stretching', 'yoga']);
            let cardioCount = 0, strengthCount = 0, workoutMobilityCount = 0;
            thisWeekWorkouts.forEach((w: any) => {
              const iconType = getWorkoutIconType(w.type || w.workout_type || '', w.activityType || '');
              if (STRENGTH_ICONS.has(iconType)) strengthCount++;
              else if (MOBILITY_ICONS.has(iconType)) workoutMobilityCount++;
              else cardioCount++;
            });
            const mobilityCount = thisWeekMobility.length + workoutMobilityCount;
            const restCount = thisWeekRestDays.length;
            
            // 🎯 JAN 25: Only show categories with count > 0, group into 2 rows
            const row1 = [
              { icon: CardioIcon, label: 'Cardio', count: cardioCount, color: 'var(--colorrehabtxt)' },
              { icon: StrengthIcon, label: 'Strength', count: strengthCount, color: 'var(--coloryogatxt)' }
            ].filter(cat => cat.count > 0);
            
            const row2 = [
              { icon: MobilityIcon, label: 'Mobility', count: mobilityCount, color: 'var(--cedar-primary)' },
              { type: 'rest', label: 'Rest', count: restCount, color: 'var(--cedar-accent-sky)' }
            ].filter(cat => cat.count > 0);
            
            const hasAnyCategories = row1.length > 0 || row2.length > 0;
            
            return hasAnyCategories && (
              <div className="flex flex-col items-center space-y-2 text-sm">
                {/* Row 1: Cardio + Strength */}
                {row1.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-6 w-full max-w-[280px]">
                    {row1.map((cat, index) => {
                      const IconComponent = cat.icon;
                      return (
                        <div key={index} className="flex items-center gap-x-2">
                          <div className="mypath-activity-icon flex-shrink-0">
                            <IconComponent size={16} color={cat.color} />
                          </div>
                          <span style={{ color: cat.color }} className="font-semibold">
                            {cat.count} {cat.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Row 2: Mobility + Rest */}
                {row2.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-6 w-full max-w-[280px]">
                    {row2.map((cat, index) => {
                      // 🔧 JAN 26: REST badge instead of palm tree icon (matches Today card)
                      if (cat.type === 'rest') {
                        return (
                          <div key={index} className="flex items-center gap-x-2">
                            <span 
                              className="text-[10px] font-semibold px-1 py-0.5 rounded flex-shrink-0"
                              style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.1)' }}
                            >
                              REST
                            </span>
                            <span style={{ color: cat.color }} className="font-semibold">
                              {cat.count} {cat.label}
                            </span>
                          </div>
                        );
                      }
                      
                      const IconComponent = cat.icon;
                      return (
                        <div key={index} className="flex items-center gap-x-2">
                          <div className="mypath-activity-icon flex-shrink-0">
                            <IconComponent size={16} color={cat.color} />
                          </div>
                          <span style={{ color: cat.color }} className="font-semibold">
                            {cat.count} {cat.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
          
          {/* 🆕 JAN 19, 2026: Removed mini activity inventory - summary is enough (matching Today card) */}

          {/* Smart Goal Attribution - Scales for multiple goals */}
          {relatedGoals.length > 1 && (
            <div className="flex items-center justify-center text-xs pt-2 border-t" style={{ color: 'var(--coloron-surface600)', borderColor: 'var(--cedar-limestone)' }}>
              <div className="flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Serves {relatedGoals.length} goals</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Simple card design for non-enhanced view
  return (
    <div className="relative">
      <div
        onClick={onTap}
        onContextMenu={(e) => {
          e.preventDefault();
          onLongPress();
        }}
        className="cursor-pointer"
      >
        <AnimaComponent
          text={`Week ${plan.weekNumber}`}
          text1={plan.title}
          weekState={plan.state === 'active' ? 'current' : 'archived'}
        />
      </div>

      {/* Week Badge */}
      <div className="absolute top-2 left-2 z-10">
        <span 
          className="text-xs px-2 py-1 rounded-full font-medium text-white"
          style={{ backgroundColor: getStatusColor() }}
        >
          Week {plan.weekNumber}
        </span>
      </div>

      {/* Status Badge */}
      <div className="absolute top-2 right-2 z-10">
        <span className={`mypath-status-badge ${
          plan.state === 'active' ? 'active' :
          plan.state === 'completed' ? 'completed' :
          'paused'
        }`}>
          {plan.state.charAt(0).toUpperCase() + plan.state.slice(1)}
        </span>
      </div>
    </div>
  );
};

// 🆕 REST DAY CARD COMPONENT (Jan 21, 2026)
// Extracted to: web/src/components/cards/RestDayCardWrapper.tsx
// Spec: specs/REST_DAY_CARD_SYSTEM_SPECIFICATION.md

const MyPathContent: React.FC = () => {
  console.log('🚨 MyPathContent: Component render started');
  
  // 🚨 HOOKS VIOLATION FIX (Jan 21, 2026): ALL useState hooks MUST be at top
  // React Error #321: These were scattered throughout component (line 2085+)
  // Rule: ALL hooks must be declared before any logic/conditions
  
  console.log('🔧 HOOK 1: viewMode');
  const [viewMode, setViewMode] = useState<ViewMode>('schedule');

  // Slider gesture: track touch start X for swipe Today ↔ Browse All
  const sliderTrackRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef<number | null>(null);

  // Entity slider (Goals | Plans | Sessions): touch start for swipe
  const entitySliderTrackRef = useRef<HTMLDivElement>(null);
  const entityTouchStartXRef = useRef<number | null>(null);
  
  console.log('🔧 HOOK 2: entityFilter');
  const [entityFilter, setEntityFilter] = useState<EntityFilter>('goals');
  
  console.log('🔧 HOOK 3: data');
  const [data, setData] = useState<HierarchicalData | null>(null);
  
  console.log('🔧 HOOK 4: chatInvitation');
  const [chatInvitation, setChatInvitation] = useState<{ workout: Workout } | null>(null);
  
  // 🔧 MINOR GAP FIX: Framework Details Modal state
  console.log('🔧 HOOK 5: frameworkModalOpen');
  const [frameworkModalOpen, setFrameworkModalOpen] = useState(false);
  
  console.log('🔧 HOOK 6: selectedFramework');
  const [selectedFramework, setSelectedFramework] = useState<{ framework: any; goalTitle: string; goalId?: string } | null>(null);
  
  // 🔧 MOVED FROM LINE 2085+ (Jan 21): Pagination and deep-linking state
  console.log('🔧 HOOK 7: currentPage');
  const [currentPage, setCurrentPage] = useState(1);
  
  console.log('🔧 HOOK 8: highlightedItemId');
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  
  console.log('🔧 HOOK 9: deepLinkType');
  const [deepLinkType, setDeepLinkType] = useState<string | null>(null);
  
  // React Router hooks (must also be at top)
  console.log('🔧 HOOK 10: navigationState');
  const { navigationState, navigateToDetail, showPeekModal, closePeekModal, goBack, resetToList } = useNavigation();
  
  console.log('🔧 HOOK 11: router');
  const router = useRouter();

  console.log('🔧 HOOK 12: pathname');
  const pathname = usePathname();

  console.log('🔧 HOOK 13: searchParams');
  const searchParams = useLocalSearchParams();
  
  console.log('✅ MyPathContent: All hooks declared successfully');

  // 🏠 HOME BUTTON PATTERN: Reset to Today when Plan tab clicked while in Plan section
  useEffect(() => {
    const resetToToday = searchParams.resetToToday === 'true';
    if (resetToToday) {
      console.log('🏠 MyPath: Resetting to Today view (home button)');

      // 🔧 FIX FEB 5, 2026: Use resetToList() to completely reset NavigationContext
      // This clears history, resets to list view, and clears all navigation state
      resetToList();

      // Reset local view state
      setViewMode('schedule'); // Reset to Today view
      setEntityFilter('goals'); // Reset to default filter

      // Close any modals
      setFrameworkModalOpen(false);
      setSelectedFramework(null);

      // Clear the search param to prevent repeated resets
      router.replace('/mypath');
    }
  }, [searchParams.resetToToday, router, resetToList]);

  // Helper to determine navigation source based on current view
  const getNavigationSource = (): import('../contexts/NavigationContext').NavigationSource => {
    if (viewMode === 'schedule') {
      return 'today';
    } else if (viewMode === 'browse') {
      if (entityFilter === 'goals') return 'browse-goals';
      if (entityFilter === 'plans') return 'browse-plans';
      if (entityFilter === 'sessions' || entityFilter === 'workouts') return 'browse-sessions';
    } else if (navigationState.currentView === 'detail') {
      if (navigationState.selectedItem?.type === 'plan') return 'plan-detail';
      if (navigationState.selectedItem?.type === 'goal') return 'goal-detail';
    }
    return 'unknown';
  };

  const handleNavigation = (tab: string) => {
    switch (tab) {
      case 'cedar':
        router.push('/chat');
        break;
      case 'my-path':
      case 'mypath':
        // 🏠 HOME BUTTON PATTERN (Jan 22, 2026): Reset to Today when Plan tab clicked
        // Even if already on MyPath page, clicking Plan tab should reset to Today view
        // This handles cases where user is in Browse view, detail view, or has modals open
        console.log('🏠 MyPath: Plan tab clicked - resetting to Today view');
        console.log('🏠 MyPath: Current navigationState.currentView =', navigationState.currentView);
        console.log('🏠 MyPath: Current viewMode =', viewMode);

        // 🔧 FIX (Feb 3, 2026): Close modal FIRST, then update state
        // Race condition: Multiple state updates + router.push() while modal overlay still rendering
        // causes full-screen z-50 overlay to block all clicks
        if (navigationState.currentView === 'detail' || navigationState.currentView === 'peekModal') {
          console.log('🏠 MyPath: Calling closePeekModal() FIRST');
          closePeekModal();

          // Wait for NavigationContext state to update before proceeding
          setTimeout(() => {
            console.log('🏠 MyPath: After closePeekModal() timeout - updating view state');
            setViewMode('schedule'); // Reset to Today view
            setEntityFilter('goals'); // Reset to default filter
            // Close any modals
            setFrameworkModalOpen(false);
            setSelectedFramework(null);
            // Navigate to /mypath to ensure we're on the base route
            router.replace('/mypath?resetToToday=true');
          }, 50); // 50ms delay for NavigationContext update
        } else {
          // No modal open - safe to update immediately
          console.log('🏠 MyPath: No modal open - updating state immediately');
          setViewMode('schedule'); // Reset to Today view
          setEntityFilter('goals'); // Reset to default filter
          // Close any modals
          setFrameworkModalOpen(false);
          setSelectedFramework(null);
          // Navigate to /mypath to ensure we're on the base route
          router.replace('/mypath?resetToToday=true');
        }
        break;
      // HEALTH SECTION TEMPORARILY REMOVED FOR MVP
      // case 'health':
      //   router.push('/health');
      //   break;
      case 'momentum':
      case 'insights':
        router.push('/insights');
        break;
      case 'settings':
        router.push('/settings');
        break;
      default:
        break;
    }
  };

  // Listen for smart back navigation events from detail pages
  useEffect(() => {
    const handleNavigateBack = (event: CustomEvent<{ source: import('../contexts/NavigationContext').NavigationSource }>) => {
      const { source } = event.detail;
      
      // Set viewMode and entityFilter based on source
      if (source === 'today') {
        setViewMode('schedule');
        setEntityFilter('goals'); // Default filter
      } else if (source === 'browse-goals') {
        setViewMode('browse');
        setEntityFilter('goals');
      } else if (source === 'browse-plans') {
        setViewMode('browse');
        setEntityFilter('plans');
      } else if (source === 'browse-sessions') {
        setViewMode('browse');
        setEntityFilter('sessions');
      }
    };
    
    if (Platform.OS === 'web') {
      window.addEventListener('navigateBackToMyPath', handleNavigateBack as EventListener);
    }
    
    return () => {
      if (Platform.OS === 'web') {
        window.removeEventListener('navigateBackToMyPath', handleNavigateBack as EventListener);
      }
    };
  }, []);

  useEffect(() => {
    const loadData = async (forceRefresh = false) => {
      console.log('🎯 MyPath: Loading hierarchical data...', forceRefresh ? '(forced refresh)' : '');
      
      // 🔄 Clear cache if forced refresh (e.g., from notification)
      if (forceRefresh) {
        console.log('🗑️ MyPath: Clearing cache before refresh');
        DataService.clearCache();
      }
      
      try {
        const hierarchicalData = await DataService.getAllData();
        console.log('📊 MyPath: Data received:', {
          goals: hierarchicalData.goals?.length || 0,
          plans: hierarchicalData.plans?.length || 0,
          workouts: hierarchicalData.workouts?.length || 0,
          mobilityRoutines: hierarchicalData.mobilityRoutines?.length || 0
        });
        setData(hierarchicalData);
        
        console.log('✅ MyPath: Hierarchical data loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load MyPath data:', error);
      }
    };

    // 🔄 Check URL for refresh parameter (from notifications - web only)
    const shouldForceRefresh = searchParams.refresh === 'true';
    if (shouldForceRefresh && Platform.OS === 'web') {
      console.log('🔄 MyPath: Force refresh requested via URL parameter');
      // Remove refresh parameter from URL
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.delete('refresh');
        window.history.replaceState({}, '', url.toString());
      }
    }

    loadData(shouldForceRefresh);

    // 🆕 DAY ROLLOVER DETECTION: Refresh data when day changes (midnight)
    // This ensures "Today" section updates automatically without browser refresh
    // 🚨 MISSION 20: Web-only feature (uses storage and document.visibilityState)
    if (Platform.OS === 'web') {
      const checkDayRollover = async () => {
        const now = new Date();
        const currentHour = now.getHours();
        
        // Check if we've crossed midnight (hour 0-1)
        // Refresh if it's early morning (0-1 AM) and we haven't refreshed today
        if (currentHour <= 1) {
          const lastRefreshDay = await storage.getItem('lastDataRefreshDay');
          const todayStr = now.toDateString();
          
          if (lastRefreshDay !== todayStr) {
            console.log('🔄 Day rollover detected - refreshing data for new day');
            await storage.setItem('lastDataRefreshDay', todayStr);
            loadData();
          }
        }
      };

      // Check immediately on mount
      checkDayRollover();

      // Set up interval to check every minute (catches midnight rollover)
      const dayCheckInterval = setInterval(checkDayRollover, 60 * 1000);

      // Also listen for visibility change (when user returns to tab/app)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
        checkDayRollover();
        // Also refresh data when user returns to app
        loadData();
      }
    };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup
      return () => {
        clearInterval(dayCheckInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
    // No cleanup needed for non-web platforms
  }, []);

  const handleWorkoutComplete = async (workout: Workout) => {
    // Check current state to determine if we're completing or unchecking
    const currentState = DataService.getWorkoutState(workout);
    
    if (currentState === 'done') {
      // Workout is currently completed, so uncheck it
      const result = DataService.markWorkoutIncomplete(workout.id);
      if (result.success) {
        console.log('✅ Workout marked as incomplete:', result.message);
        // Refresh data
        DataService.getAllData().then(setData);
      } else {
        console.error('❌ Failed to mark workout incomplete:', result.message);
      }
    } else {
      // Workout is not completed, so complete it
      const result = DataService.markWorkoutComplete(workout.id);
      if (result.success) {
        console.log('✅ Workout completed:', result.message);
        
        // POST-WORKOUT CHAT: previously fetched the legacy
        // post-workout-completion endpoint to trigger Cedar's debrief.
        // That endpoint was removed 2026-05-04 (bypassed unified
        // pipeline). Web is legacy per CLAUDE.md; native uses the
        // start-debrief endpoint via the shared <DebriefButton>.
        // Web check-off completions no longer fire an immediate debrief
        // — acceptable per the legacy-web policy.

        // Refresh data
        DataService.getAllData().then(setData);
      } else {
        console.error('❌ Failed to complete workout:', result.message);
      }
    }
  };

  const handleChatInvitation = () => {
    if (chatInvitation) {
      // Navigate to chat with workout context
      if (Platform.OS === 'web') {
        window.location.href = `/chat?context=workout&id=${chatInvitation.workout.id}&action=debrief&message=I just completed "${chatInvitation.workout.title}". Want to hear how it went?`;
      } else {
        // On native, use router navigation
        router.push(`/chat?context=workout&id=${chatInvitation.workout.id}&action=debrief&message=${encodeURIComponent(`I just completed "${chatInvitation.workout.title}". Want to hear how it went?`)}`);
      }
    }
    setChatInvitation(null);
  };

  const handleItemClick = (type: string, id: string) => {
    // 🔧 FIX JAN 23: navigateToDetail() already handles React Router navigation
    // Don't call navigate() again - causes double navigation and blank screen
    const source = getNavigationSource();
    navigateToDetail(type as any, id, source);
  };

  // 🔧 MOVED TO TOP (Jan 21): Pagination and deep-linking state now declared at line ~1850

  // Initialize page and deep-linking from URL params
  useEffect(() => {
    const page = parseInt((searchParams.page as string) || '1');
    setCurrentPage(page);

    // Handle deep-linking from plan notifications
    const highlightId = searchParams.highlight as string | undefined;
    const linkType = searchParams.type as string | undefined;
    
    if (highlightId && linkType) {
      setHighlightedItemId(highlightId);
      setDeepLinkType(linkType);
      
      // Auto-navigate to the correct view for the highlighted item
      if (linkType === 'plan') {
        setEntityFilter('plans');
      }
      
      // Clear highlighting after 3 seconds
      setTimeout(() => {
        setHighlightedItemId(null);
        setDeepLinkType(null);
        // Clean up URL params
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.delete('highlight');
          url.searchParams.delete('type');
          window.history.replaceState({}, '', url.toString());
        }
      }, 3000);
    }
  }, [searchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.history.replaceState({}, '', url.toString());
    }
  }, [entityFilter]);

  const getCurrentData = (): any[] => {
    // 🔧 FIX (Jan 2025): Use actual data state instead of cached data
    if (!data) return [];
    
    const hierarchicalData = data;
    
    // Extract the specific array for the selected entity
    let rawData: any[] = [];
    switch (entityFilter) {
      case 'goals':
        rawData = hierarchicalData.goals;
        console.log(`🔍 DEBUG: Goals count = ${rawData?.length || 0}`, rawData);
        break;
      case 'plans':
        rawData = hierarchicalData.plans;
        console.log(`🔍 DEBUG: Plans count = ${rawData?.length || 0}`, rawData);
        break;
      case 'sessions':
        // 🆕 JAN 2025: Unified sessions view combines workouts + mobility in reverse chronological order
        // 🆕 JAN 2025: Filter out minor activities (daily mobility, rest days) - they show in plan but NOT in history
        const workouts = (hierarchicalData.workouts || []).filter((w: any) => {
          // 🔧 FEB 2 (Bug #61): Filter out rest_day workouts + daily activities
          // Workout Reconciliation: hide replaced/skipped workouts from sessions list
          const state = DataService.getWorkoutState(w);
          return state !== 'hidden' && w.saveToHistory !== false && w.context !== 'daily' && w.context !== 'rest_day';
        });
        const mobility = (hierarchicalData.mobilityRoutines || []).filter((m: any) => {
          // 🔧 FIX JAN 29: Filter out component mobility (warmup/cooldown) + daily routines + rest_day activities
          return m.saveToHistory !== false && 
                 m.context !== 'daily' && 
                 m.context !== 'component' &&
                 m.context !== 'rest_day' && // 🆕 FEB 2 (Bug #61)
                 !(m as any).workoutId &&
                 !(m as any).workout_id;
        });
        
        // Add type markers for rendering
        const workoutsWithType = workouts.map((w: any) => ({ ...w, sessionType: 'workout' }));
        const mobilityWithType = mobility.map((m: any) => ({ ...m, sessionType: 'mobility' }));
        
        rawData = [...workoutsWithType, ...mobilityWithType];
        console.log(`🔍 DEBUG: Sessions count = ${rawData?.length || 0} (${workouts.length} workouts + ${mobility.length} mobility) - minor activities filtered out`);
        break;
      default:
        rawData = [];
    }
    
    // 🆕 JAN 2025: Always sort in reverse chronological order
    let sortedData = rawData;
    switch (entityFilter) {
      case 'sessions':
        // 🔧 FIX (Dec 6, 2025): Sort sessions by scheduled date ASCENDING (nearest on top, future below)
        // Also add secondary sort by order field for same-day sessions (e.g., post-run release after run)
        sortedData = rawData.sort((a, b) => {
          const dateA = a.scheduledDate || a.created_at || '';
          const dateB = b.scheduledDate || b.created_at || '';
          const dateCompare = new Date(dateA).getTime() - new Date(dateB).getTime();
          
          // If same day, sort by order field (lower order first)
          if (dateCompare === 0) {
            const orderA = (a as any).order || (a as any).session_order || 0;
            const orderB = (b as any).order || (b as any).session_order || 0;
            return orderA - orderB;
          }
          
          return dateCompare;
        });
        break;
      case 'plans':
        // Plans sorted by weekStartDate (reverse chronological)
        sortedData = (rawData as Plan[]).sort((a, b) => 
          new Date(b.weekStartDate).getTime() - new Date(a.weekStartDate).getTime()
        );
        break;
      case 'goals':
        // Goals sorted by updatedAt (reverse chronological)
        sortedData = (rawData as Goal[]).sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        break;
      default:
        sortedData = rawData;
    }
    
    return sortedData;
  };

  // Pagination logic for performance (simplified - always show all for now)
  const paginatedData = useMemo(() => {
    const allData = getCurrentData();
    
    // Show all items (pagination can be added later if needed)
    return {
      items: allData,
      totalItems: allData.length,
      totalPages: 1,
      currentPage: 1,
      hasMore: false
    };
  }, [entityFilter, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', page.toString());
      } else {
        url.searchParams.delete('page');
      }
      window.history.replaceState({}, '', url.toString());
    }
    // Scroll to top when changing pages (web only)
    if (Platform.OS === 'web') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!data) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="p-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-32 mb-4"></div>
            <div className="space-y-3">
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
        
        {/* Bottom Navigation */}
        <div className="navigation-container">
          <ProperNavCoach 
            activeTab="mypath"
            className=""
            onClickTab={handleNavigation}
          />
        </div>
      </div>
    );
  }

  // Render detail pages - using clean components without bottom navigation
  if (navigationState.currentView === 'detail' && navigationState.selectedItem) {
    const { type, id } = navigationState.selectedItem;
    
    // 🔧 DEFENSIVE FIX (Jan 21, 2026): Ensure id exists before rendering detail
    if (!id) {
      console.error('❌ MyPath: selectedItem has no id!', navigationState.selectedItem);
      // Just return null - don't call setState before return (hooks violation)
      // NavigationProvider defensive handling will catch this
      return null;
    }
    
    // 🔧 FIX FEB 4, 2026: Add key prop to force full unmount/mount on navigation
    // Prevents reconciliation issues when switching between detail types
    // Key format: {type}-{id} ensures React knows this is a NEW component
    const componentKey = `${type}-${id}`;
    
    switch (type) {
      case 'workout':
        return <WorkoutDetail key={componentKey} workoutId={id} />;
      case 'mobility':
        return <MobilityDetail key={componentKey} routineId={id} />;
      case 'plan':
        return <PlanDetail key={componentKey} planId={id} />;
      case 'goal':
        return <GoalDetail key={componentKey} goalId={id} />;
      case 'restDay': // 🛌 JAN 23, 2026: Add rest day detail rendering
        return <RestDayDetail key={componentKey} restDayId={id} />;
      default:
        return null;
    }
  }

  // Render peek modals
  if (navigationState.currentView === 'peekModal' && navigationState.peekModalItem) {
    console.log('🚨 MyPath: Rendering PEEK MODAL OVERLAY (z-50, blocks all clicks)');
    console.log('🚨 MyPath: navigationState.currentView =', navigationState.currentView);
    console.log('🚨 MyPath: peekModalItem =', navigationState.peekModalItem);
    
    const renderPeekModal = () => {
      switch (navigationState.peekModalItem?.type) {
        case 'plan':
          return <AnimaPlanPeekModal />;
        case 'workout':
          return <AnimaWorkoutPeekModal />;
        case 'mobility':
          return <AnimaMobilityPeekModal />;
        default:
          return null;
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={closePeekModal}>
        <div className="bg-white rounded-t-lg w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          {renderPeekModal()}
        </div>
      </div>
    );
  }

  const renderScheduleView = () => {
    return (
      <div className="space-y-6">
        <div className="px-4">
          <ThisWeeksPlanCard
            onItemClick={handleItemClick}
          />
        </div>
        <TodaysFocus
          onItemClick={handleItemClick}
          onWorkoutComplete={handleWorkoutComplete}
        />
        <ActiveGoalsSection
          data={data}
          onNavigateToGoals={() => {
            setViewMode('browse');
            setEntityFilter('goals');
          }}
          onGoalClick={(goalId) => handleItemClick('goal', goalId)}
        />
      </div>
    );
  };

  const renderBrowseView = () => {
    const currentData = getCurrentData();
    
    if (currentData.length === 0) {
      const getEmptyStateContent = () => {
        switch (entityFilter) {
          case 'goals':
            return "Your goals for fitness, whether it's running a 5K, improving flexibility, or building strength, will show up here as you chat with Cedar about them.";
          case 'plans':
            return "Cedar makes plans in week-long increments based on your goals. You can check them out here once you chat with Cedar about your goals and approach to your active life.";
          case 'sessions':
            return "Workouts and mobility routines are what make up your weekly plans. Explore them here individually. This will be built out as you talk with Cedar.";
          default:
            return `You don't have any ${entityFilter} yet.`;
        }
      };
      
      return (
        <div className="text-center py-12 px-6">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div className="text-gray-600 text-sm leading-relaxed max-w-sm mx-auto">
            {getEmptyStateContent()}
          </div>
          {entityFilter !== 'goals' && (
            <button
              type="button"
              onClick={() => router.push('/chat')}
              className="mypath-empty-chat-cta"
            >
              <ChatWithCedarIcon
                size="small"
                className="mypath-empty-chat-cta-icon"
                style={{ color: 'white' }}
              />
              <span>Chat with Cedar about this</span>
            </button>
          )}
        </div>
      );
    }

    // Deep-link highlight wrapper component
    const HighlightWrapper: React.FC<{
      children: React.ReactNode;
      isHighlighted: boolean;
      itemId: string;
    }> = ({ children, isHighlighted, itemId }) => {
      if (!isHighlighted) {
        return <>{children}</>;
      }

      return (
        <div 
          id={`highlight-${itemId}`}
          className="relative"
        >
          {/* Highlight border animation */}
          <div className="absolute inset-0 rounded-lg border-4 border-blue-500 animate-pulse z-10 pointer-events-none" />
          {children}
        </div>
      );
    };

    return (
      <div className="space-y-3">
        {currentData.map((item) => {
          const isHighlighted = highlightedItemId === item.id && deepLinkType === entityFilter.slice(0, -1); // Remove 's' from plural
          
          const cardElement = (() => {
            switch (entityFilter) {
              case 'goals':
                return (
                  <HighlightWrapper
                    isHighlighted={isHighlighted}
                    itemId={item.id}
                  >
                    <GoalCardWrapper
                      key={item.id} 
                      goal={item as Goal}
                      isArchived={false}
                      onTap={() => navigateToDetail('goal', item.id, getNavigationSource())}
                      onLongPress={() => showPeekModal('goal', item.id)}
                      onViewFramework={(framework, goalTitle) => {
                        setSelectedFramework({ framework, goalTitle, goalId: item.id });
                        setFrameworkModalOpen(true);
                      }}
                    />
                  </HighlightWrapper>
                );
              case 'plans':
                return (
                  <HighlightWrapper
                    isHighlighted={isHighlighted}
                    itemId={item.id}
                  >
                    <PlanCardWrapper
                      key={item.id} 
                      plan={item as Plan}
                      isArchived={false}
                      onTap={() => navigateToDetail('plan', item.id, getNavigationSource())}
                      onLongPress={() => showPeekModal('plan', item.id)}
                      enhanced={true}
                    />
                  </HighlightWrapper>
                );
              case 'sessions':
                // 🆕 JAN 2025: Unified sessions view - render workout or mobility card based on sessionType
                const sessionType = (item as any).sessionType;
                if (sessionType === 'workout') {
                  return (
                    <HighlightWrapper
                      isHighlighted={isHighlighted}
                      itemId={item.id}
                    >
                      <SimpleWorkoutCard
                        key={item.id} 
                        workout={item as Workout}
                        isArchived={false}
                        onTap={() => navigateToDetail('workout', item.id, getNavigationSource())}
                        onLongPress={() => showPeekModal('workout', item.id)}
                        onComplete={handleWorkoutComplete}
                        showTodayBadge={false}
                        showDateBadge={true}
                      />
                    </HighlightWrapper>
                  );
                } else {
                  return (
                    <HighlightWrapper
                      isHighlighted={isHighlighted}
                      itemId={item.id}
                    >
                      <MobilityCardWrapper
                        key={item.id} 
                        routine={item as MobilityRoutine}
                        isArchived={false}
                        onTap={() => navigateToDetail('mobility', item.id, getNavigationSource())}
                        onLongPress={() => showPeekModal('mobility', item.id)}
                        showTodayBadge={false}
                        showDateBadge={true}
                      />
                    </HighlightWrapper>
                  );
                }
              default:
                return null;
            }
          })();

          return cardElement;
        })}
      </div>
    );
  };

  const getEntityDisplayName = (entity: EntityFilter) => {
    switch (entity) {
      case 'goals': return 'Goals';
      case 'plans': return 'Plans';
      case 'sessions': return 'Sessions'; // 🆕 JAN 2025: Simplified to 3 filters
    }
  };

  return (
    <div className="clean-chat-container mypath-container flex flex-col h-screen bg-white max-w-md mx-auto">
      <div className="flex flex-col h-full bg-white">

        {/* Fixed Top Navigation Area (never scrolls) */}
        <div className="mypath-fixed-top">
          {/* Coach Cedar link - min 44px touch target */}
          <div className="mypath-coach-cedar-link-row">
            <button
              type="button"
              className="mypath-coach-cedar-link"
              onClick={() => router.push('/chat')}
              aria-label="Open Coach Cedar chat"
            >
              <ChatWithCedarIcon className="mypath-coach-cedar-link-icon" />
              <span>Coach Cedar</span>
            </button>
          </div>
          {/* View Mode Toggle - Slider with swipe/drag gesture */}
          <div className="px-4 pt-4 pb-2" style={{ paddingBottom: '10px' }}>
          <div
            ref={sliderTrackRef}
            className="mypath-view-slider-track"
            role="tablist"
            aria-label="View mode"
            onTouchStart={(e) => {
              touchStartXRef.current = e.targetTouches[0].clientX;
            }}
            onTouchEnd={(e) => {
              const startX = touchStartXRef.current;
              if (startX == null) return;
              const endX = e.changedTouches[0].clientX;
              const deltaX = endX - startX;
              const threshold = 40;
              if (deltaX < -threshold) setViewMode('browse');
              else if (deltaX > threshold) setViewMode('schedule');
              touchStartXRef.current = null;
            }}
            onMouseDown={(e) => {
              touchStartXRef.current = e.clientX;
            }}
            onMouseUp={(e) => {
              const startX = touchStartXRef.current;
              if (startX == null) return;
              const endX = e.clientX;
              const deltaX = endX - startX;
              const threshold = 40;
              if (deltaX < -threshold) setViewMode('browse');
              else if (deltaX > threshold) setViewMode('schedule');
              touchStartXRef.current = null;
            }}
            onMouseLeave={() => {
              touchStartXRef.current = null;
            }}
          >
            <div
              className="mypath-view-slider-pill"
              style={{ transform: viewMode === 'browse' ? 'translateX(100%)' : 'translateX(0)' }}
              aria-hidden
            />
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'schedule'}
              className={`mypath-view-slider-segment ${viewMode === 'schedule' ? 'mypath-view-slider-active' : ''}`}
              onClick={() => setViewMode('schedule')}
            >
              My Week
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === 'browse'}
              className={`mypath-view-slider-segment ${viewMode === 'browse' ? 'mypath-view-slider-active' : ''}`}
              onClick={() => setViewMode('browse')}
            >
              Browse All
            </button>
          </div>
        </div>

        {/* Browse Mode Filters - Entity slider (Goals | Plans | Sessions) with swipe */}
        {viewMode === 'browse' && (
          <div className="flex-shrink-0">
            <div className="px-4 pb-3">
              <div
                ref={entitySliderTrackRef}
                className="mypath-entity-slider-track"
                role="tablist"
                aria-label="Entity filter"
                onTouchStart={(e) => {
                  entityTouchStartXRef.current = e.targetTouches[0].clientX;
                }}
                onTouchEnd={(e) => {
                  const startX = entityTouchStartXRef.current;
                  if (startX == null) return;
                  const endX = e.changedTouches[0].clientX;
                  const deltaX = endX - startX;
                  const threshold = 40;
                  const order: EntityFilter[] = ['goals', 'plans', 'sessions'];
                  const idx = order.indexOf(entityFilter);
                  if (deltaX < -threshold && idx < 2) setEntityFilter(order[idx + 1]);
                  else if (deltaX > threshold && idx > 0) setEntityFilter(order[idx - 1]);
                  entityTouchStartXRef.current = null;
                }}
                onMouseDown={(e) => {
                  entityTouchStartXRef.current = e.clientX;
                }}
                onMouseUp={(e) => {
                  const startX = entityTouchStartXRef.current;
                  if (startX == null) return;
                  const endX = e.clientX;
                  const deltaX = endX - startX;
                  const threshold = 40;
                  const order: EntityFilter[] = ['goals', 'plans', 'sessions'];
                  const idx = order.indexOf(entityFilter);
                  if (deltaX < -threshold && idx < 2) setEntityFilter(order[idx + 1]);
                  else if (deltaX > threshold && idx > 0) setEntityFilter(order[idx - 1]);
                  entityTouchStartXRef.current = null;
                }}
                onMouseLeave={() => {
                  entityTouchStartXRef.current = null;
                }}
              >
                <div
                  className="mypath-entity-slider-pill"
                  style={{
                    transform:
                      entityFilter === 'goals'
                        ? 'translateX(0)'
                        : entityFilter === 'plans'
                        ? 'translateX(100%)'
                        : 'translateX(200%)',
                  }}
                  aria-hidden
                />
                {(['goals', 'plans', 'sessions'] as EntityFilter[]).map((entity) => (
                  <button
                    key={entity}
                    type="button"
                    role="tab"
                    aria-selected={entityFilter === entity}
                    className={`mypath-entity-slider-segment ${entityFilter === entity ? 'mypath-entity-slider-active' : ''}`}
                    onClick={() => setEntityFilter(entity)}
                  >
                    {getEntityDisplayName(entity)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        </div>
        {/* End Fixed Top Navigation */}

        {/* Scrollable Content Area (only this scrolls) */}
        <div className="mypath-scrollable-content px-4">

          
          {viewMode === 'schedule' ? renderScheduleView() : renderBrowseView()}
        </div>
        
        {/* Bottom Navigation - Fixed at bottom */}
        <div 
          className="navigation-container fixed bottom-0 left-1/2 transform -translate-x-1/2 bg-white border-t border-gray-200"
          style={{ width: '100%', maxWidth: '448px', zIndex: 100 }}
        >
          <ProperNavCoach 
            activeTab="mypath"
            className=""
            onClickTab={handleNavigation}
          />
        </div>
      </div>

      {/* Chat Invitation Modal */}
      {chatInvitation && (
        <ChatInvitationModal
          workout={chatInvitation.workout}
          onChat={handleChatInvitation}
          onDismiss={() => setChatInvitation(null)}
        />
      )}
      
      {/* 🔧 MINOR GAP FIX: Framework Details Modal */}
      {selectedFramework && (
        <FrameworkDetailsModal
          isOpen={frameworkModalOpen}
          onClose={() => {
            setFrameworkModalOpen(false);
            setSelectedFramework(null);
          }}
          framework={selectedFramework.framework}
          goalTitle={selectedFramework.goalTitle}
          goalId={selectedFramework.goalId}
        />
      )}
    </div>
  );
};

const MyPath: React.FC = () => {
  return (
    <NavigationProvider>
      <MyPathContent />
    </NavigationProvider>
  );
};

export default MyPath;
